import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { nhost } from "@/lib/nhost";
import { MOMO_OPEN_EVENT, type MomoPayRequest } from "@/hooks/usePaystackPayment";

// 📱 The MoMo payment window. It lives once in App.tsx and opens when a page
// calls triggerMomoPayment(). Steps:
//   1. Show our MoMo number, the exact amount and the reference
//   2. Customer sends the money, then types the Transaction ID from their SMS
//   3. We wait for an admin to confirm (checking every 10 seconds)
type Stage = "pay" | "checking" | "confirmed" | "rejected";

function CopyRow({ label, value, big }: { label: string; value: string; big?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2 bg-foreground-50 rounded-xl px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">{label}</p>
        <p className={`${big ? "text-lg font-extrabold font-sora" : "text-sm font-bold"} text-foreground-900 break-words`}>{value}</p>
      </div>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        className="text-xs font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
      >
        {copied ? "Copied ✓" : "Copy"}
      </button>
    </div>
  );
}

export default function MomoPayModal() {
  const [req, setReq] = useState<MomoPayRequest | null>(null);
  const [stage, setStage] = useState<Stage>("pay");
  const [txn, setTxn] = useState("");
  const [sender, setSender] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const doneRef = useRef(false);

  // Open when a page asks for it
  useEffect(() => {
    const open = (e: Event) => {
      const detail = (e as CustomEvent<MomoPayRequest>).detail;
      if (!detail) return;
      doneRef.current = false;
      setReq(detail);
      setStage("pay");
      setTxn("");
      setSender("");
      setError("");
      setMessage("");
    };
    window.addEventListener(MOMO_OPEN_EVENT, open);
    return () => window.removeEventListener(MOMO_OPEN_EVENT, open);
  }, []);

  // While waiting, ask every 10 seconds whether an admin has confirmed it
  useEffect(() => {
    if (!req || stage !== "checking") return;
    const check = async () => {
      try {
        const r = await nhost.functions.post("/momo-payment", { action: "status", reference: req.reference });
        const b = r.body as { stage?: string; message?: string };
        if (b.message) setMessage(b.message);
        if (b.stage === "confirmed" && !doneRef.current) {
          doneRef.current = true;
          setStage("confirmed");
          setTimeout(() => {
            req.onSuccess(req.reference);
            setReq(null);
          }, 1500);
        } else if (b.stage === "rejected") {
          setStage("rejected");
        }
      } catch {
        /* try again next time */
      }
    };
    check();
    const t = setInterval(check, 10000);
    return () => clearInterval(t);
  }, [req, stage]);

  if (!req) return null;

  const submit = async () => {
    setError("");
    if (txn.replace(/[^a-zA-Z0-9]/g, "").length < 6) {
      setError("Please enter the Transaction ID from your MoMo SMS.");
      return;
    }
    setBusy(true);
    try {
      const r = await nhost.functions.post("/momo-payment", {
        action: "submit",
        reference: req.reference,
        transaction_id: txn,
        sender_phone: sender,
      });
      const b = r.body as { message?: string };
      setMessage(b.message || "");
      setStage("checking");
    } catch (e: unknown) {
      const msg = (e as { body?: { error?: unknown } })?.body?.error;
      setError(typeof msg === "string" ? msg : "Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 px-3 py-3">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-yellow-100">
              <i className="ri-smartphone-line text-yellow-700"></i>
            </div>
            <h2 className="font-bold text-foreground-900">Pay with MoMo</h2>
          </div>
          <button onClick={() => setReq(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-foreground-50 cursor-pointer" aria-label="Close">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {stage === "pay" && (
          <div className="px-5 pb-5 space-y-3">
            <p className="text-sm text-foreground-600">
              <strong>1.</strong> Send <strong>exactly</strong> this amount by Mobile Money to our number:
            </p>
            <CopyRow label="Amount" value={`GH₵${req.amount.toFixed(2)}`} big />
            <CopyRow label="MoMo number" value={req.momo.number} big />
            <div className="bg-foreground-50 rounded-xl px-3 py-2.5">
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Name you'll see</p>
              <p className="text-sm font-bold text-foreground-900">{req.momo.name}</p>
            </div>
            <CopyRow label="Reference (type this in MoMo)" value={req.momo.reference_text} />
            <p className="text-[11px] text-foreground-500">
              On MTN: dial <strong>*170#</strong> → Transfer Money → MoMo User, or use the MoMo app. Check the name matches before you confirm.
            </p>

            <p className="text-sm text-foreground-600 pt-2">
              <strong>2.</strong> After paying, enter the <strong>Transaction ID</strong> from your MoMo SMS:
            </p>
            <input
              value={txn}
              onChange={(e) => setTxn(e.target.value)}
              placeholder="e.g. 51234567890"
              maxLength={30}
              autoComplete="off"
              className="w-full border border-foreground-200 rounded-xl px-4 py-3 font-mono tracking-wide focus:outline-none focus:border-primary-400"
            />
            <input
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="Number you paid from (optional)"
              inputMode="tel"
              maxLength={15}
              className="w-full border border-foreground-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-400"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              onClick={submit}
              disabled={busy}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-3.5 rounded-xl cursor-pointer disabled:opacity-50"
            >
              {busy ? "Sending..." : "I've paid"}
            </button>
            <p className="text-[11px] text-foreground-400 text-center">
              Order #{req.reference} · Not paid within 24 hours? It's cancelled automatically.
            </p>
          </div>
        )}

        {stage === "checking" && (
          <div className="px-5 pb-6 text-center">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto my-4"></div>
            <h3 className="font-bold text-foreground-900 mb-1">Checking your payment…</h3>
            <p className="text-sm text-foreground-600 mb-4">{message || "This usually takes less than 10 minutes."}</p>
            <p className="text-xs text-foreground-500 mb-4">
              You can close this window. Your order <strong>#{req.reference}</strong> will be completed as soon as we confirm the payment.
            </p>
            <Link to="/track-order" onClick={() => setReq(null)} className="text-sm font-bold text-primary-600">
              Track my order →
            </Link>
          </div>
        )}

        {stage === "confirmed" && (
          <div className="px-5 pb-6 text-center">
            <i className="ri-checkbox-circle-line text-primary-600 text-5xl"></i>
            <h3 className="font-bold text-foreground-900 mt-2 mb-1">Payment confirmed! 🎉</h3>
            <p className="text-sm text-foreground-600">{message}</p>
          </div>
        )}

        {stage === "rejected" && (
          <div className="px-5 pb-6 text-center">
            <i className="ri-error-warning-line text-red-500 text-5xl"></i>
            <h3 className="font-bold text-foreground-900 mt-2 mb-1">We couldn't confirm this payment</h3>
            <p className="text-sm text-foreground-600 mb-4">{message}</p>
            <p className="text-xs text-foreground-500">If you did pay, message us in Live Chat with your order number #{req.reference} and Transaction ID.</p>
          </div>
        )}
      </div>
    </div>
  );
}
